<template>
  <TransitionFromTop>
    <div :class="[style.informationAnswer, mobileVersion ? style.mobile : '']">
      <img v-if="isImage" :src="imageSrc" alt="Загруженное изображение" />

      <video
        v-else-if="isVideo"
        :src="imageSrc"
        playsinline
        webkit-playsinline
        preload="auto"
        autoplay
        muted
        loop
      />

      <label :for="inputId"></label>

      <input
        type="file"
        accept="image/*, video/*"
        :id="inputId"
        @change="setImg"
      />

      <button-cross :class="style.crossButton" @click="delImg" />
    </div>
  </TransitionFromTop>
</template>

<script lang="ts">
import {defineComponent} from "vue";

import style from "./InformationAnswer.module.scss";
import {DOMAIN} from "@/http";

import ButtonCross from "@/elements/buttonCross/buttonCross.vue";
import TransitionFromTop from "@/dynamics/transitions/TransitionFromTop/TransitionFromTop.vue";

import {questions} from "@/page/quiz/typeQuiz";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";

export default defineComponent({
  name: "InformationAnswer",

  components: {
    ButtonCross,
    TransitionFromTop
  },

  data() {
    return {
      style,
      DOMAIN,
      modifyQuiz,
      localImg: "" as string,
      cachedFilePreviewList: {} as Record<string, string>
    };
  },

  computed: {
    quiz() {
      return this.$quizStore.getQuiz;
    },

    selectedQuestion(): number {
      return this.$quizStore.getSelectedQuestion;
    },

    selectedVersion(): string {
      return this.$quizStore.getSelectedVersion;
    },

    mobileVersion(): boolean {
      return this.selectedVersion === "Мобильная";
    },

    currentQuestion(): questions {
      const question = this.modifyQuiz("get", "", [
        "questions",
        this.selectedQuestion
      ]);

      if (
        question &&
        typeof question === "object" &&
        !Array.isArray(question)
      ) {
        return question as questions;
      }

      return {} as questions;
    },

    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles;
    },

    inputId(): string {
      return `information-image-${this.selectedQuestion}`;
    },

    informationPath(): string {
      return this.currentQuestion.information || "";
    },

    imageSrc(): string {
      if (this.localImg) {
        return this.localImg;
      }

      const path = this.informationPath;

      if (!path) {
        return `https://${this.DOMAIN}/api/img?name=logoWidget.svg`;
      }

      if (path.startsWith("blob:") || path.startsWith("data:")) {
        return path;
      }

      if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
      }

      const cachedPreview = this.getCachedFilePreview(path);

      if (cachedPreview) {
        return cachedPreview;
      }

      return `https://${this.DOMAIN}/api/img?name=${encodeURIComponent(path)}`;
    },

    isImage(): boolean {
      const info = this.informationPath;

      if (!info) {
        return true;
      }

      if (info.startsWith("blob:") || info.startsWith("data:")) {
        return true;
      }

      return /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(info);
    },

    isVideo(): boolean {
      const info = this.informationPath;

      return /\.(mp4|webm|ogg)$/i.test(info);
    }
  },

  watch: {
    informationPath(newValue: string, oldValue: string): void {
      if (oldValue && oldValue !== newValue) {
        this.revokeCachedPreview(oldValue);
      }

      if (!newValue) {
        this.revokeLocalImage();
        this.localImg = "";
        return;
      }

      if (newValue !== this.localImg) {
        this.revokeLocalImage();
        this.localImg = "";
      }
    },

    selectedQuestion(): void {
      this.revokeLocalImage();
      this.revokeAllCachedPreviews();

      this.localImg = "";
      this.cachedFilePreviewList = {};
    }
  },

  methods: {
    delImg(): void {
      const currentImage = this.informationPath;

      this.revokeLocalImage();
      this.revokeCachedPreview(currentImage);
      this.removeCachedFile(currentImage);

      this.localImg = "";
      this.setInformationValue("");

      const input = document.getElementById(
        this.inputId
      ) as HTMLInputElement | null;

      if (input) {
        input.value = "";
      }
    },

    async setImg(event: Event): Promise<void> {
      const target = event.target as HTMLInputElement;

      if (!target.files?.length) {
        return;
      }

      const file = target.files[0];
      const oldImage = this.informationPath;
      const blob = file.slice(0, file.size, file.type);
      const newName = this.createInformationFileName(file);
      const newFile = new File([blob], newName, {type: file.type});
      const srcFile = URL.createObjectURL(newFile);

      this.revokeLocalImage();
      this.revokeCachedPreview(oldImage);
      this.revokeCachedPreview(newFile.name);
      this.removeCachedFile(oldImage);
      this.removeCachedFile(newFile.name);

      this.localImg = srcFile;
      this.setInformationValue(newFile.name);

      this.cachedFiles.push(newFile);
      target.value = "";
    },

    createInformationFileName(file: File): string {
      const ext = file.name.split(".").pop() || "";
      const questionType = this.currentQuestion.type || "information";
      const uniqueSuffix = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      return `${this.selectedQuestion}-${questionType}-${uniqueSuffix}.${ext}`;
    },

    setInformationValue(value: string): void {
      this.modifyQuiz("set", value, [
        "questions",
        this.selectedQuestion,
        "information"
      ]);
    },

    getCachedFilePreview(fileName: string): string {
      if (!fileName) {
        return "";
      }

      const savedPreview = this.cachedFilePreviewList[fileName];

      if (savedPreview) {
        return savedPreview;
      }

      const cachedFile = this.cachedFiles.find(
        (file) => file.name === fileName
      );

      if (!cachedFile) {
        return "";
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.cachedFilePreviewList = {
        ...this.cachedFilePreviewList,
        [fileName]: previewUrl
      };

      return previewUrl;
    },

    removeCachedFile(fileName: string): void {
      if (!fileName) {
        return;
      }

      const index = this.cachedFiles.findIndex(
        (file) => file.name === fileName
      );

      if (index !== -1) {
        this.cachedFiles.splice(index, 1);
      }
    },

    revokeLocalImage(): void {
      if (this.localImg?.startsWith("blob:")) {
        URL.revokeObjectURL(this.localImg);
      }
    },

    revokeCachedPreview(fileName: string): void {
      if (!fileName) {
        return;
      }

      const previewUrl = this.cachedFilePreviewList[fileName];

      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      const updatedPreviewList = {...this.cachedFilePreviewList};

      delete updatedPreviewList[fileName];

      this.cachedFilePreviewList = updatedPreviewList;
    },

    revokeAllCachedPreviews(): void {
      Object.values(this.cachedFilePreviewList).forEach((previewUrl) => {
        if (previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }
      });
    }
  },

  beforeUnmount() {
    this.revokeLocalImage();
    this.revokeAllCachedPreviews();
  }
});
</script>
